/**
 * Label component for the Indigo design system.
 *
 * A polymorphic text component specifically for labels with two size variants.
 * Label styles match H4 (md) and H5 (sm) heading styles as specified in the design system.
 * Supports rendering as different HTML elements while maintaining consistent typography styles.
 *
 * Features:
 * - Two size variants: md (same as H4), sm (same as H5)
 * - Polymorphic component - can render as different elements via 'as' prop
 * - Design system typography styles
 * - Support for Tailwind utility classes via className prop
 * - TypeScript-first with proper prop types
 * - Forwards ref for DOM manipulation
 *
 * @example
 * ```tsx
 * // Default: renders as <span> with medium size
 * <Label>Field Label</Label>
 *
 * // Small label
 * <Label size="sm">Small Label</Label>
 *
 * // Render as label element for form fields
 * <Label as="label" htmlFor="email">Email Address</Label>
 *
 * // With additional Tailwind utilities
 * <Label size="sm" className="text-indigo-text-secondary mb-2">
 *   Optional Field
 * </Label>
 * ```
 */
import { cva } from "class-variance-authority";
import { ComponentPropsWithRef, ElementType, forwardRef } from "react";
import "./Typography.css";

const labelVariants = cva("indigo-typography-base indigo-typography-label", {
  variants: {
    size: {
      md: "indigo-typography-label-md",
      sm: "indigo-typography-label-sm",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

type LabelOwnProps<E extends ElementType = ElementType> = {
  /**
   * The HTML element to render as
   * @default "span"
   */
  as?: E;

  /**
   * The size variant of the label
   * - md: Same as H4 (12px, bold)
   * - sm: Same as H5 (11px, bold)
   * @default "md"
   */
  size?: "md" | "sm";

  /**
   * Additional CSS classes to apply
   */
  className?: string;

  /**
   * The content to display
   */
  children?: React.ReactNode;
};

export type LabelProps<E extends ElementType = "span"> = LabelOwnProps<E> &
  Omit<ComponentPropsWithRef<E>, keyof LabelOwnProps>;

/**
 * Label component
 *
 * Renders a polymorphic text element with consistent label typography styles.
 *
 * @param props - Component props (@see LabelProps)
 * @returns A React text component (span, label, div, etc.)
 */
export const Label = forwardRef(
  <E extends ElementType = "span">(
    { as, size = "md", className, children, ...props }: LabelProps<E>,
    ref: React.Ref<any>,
  ) => {
    const Component = as || "span";
    const labelClass = labelVariants({ size });
    const combinedClassName = className
      ? `${labelClass} ${className}`
      : labelClass;

    return (
      <Component ref={ref} className={combinedClassName} {...props}>
        {children}
      </Component>
    );
  },
);

Label.displayName = "Label";

export { labelVariants };
